import EventDetails from "../stores/domainStore/pages/models/eventDetails";
import {EventStatus} from "./enums/eventStatus";
import {format} from "date-fns";
import {EventPaidType} from "./enums/eventPaidType";
import {TFunction} from "react-i18next";
import Tag, {TagType} from "../components/atoms/tag/tag";
import React from "react";
import {RecurringStatus} from "./enums/RecurringStatus";
import {IconSource} from "./enums/iconSource";
import Icon from "../components/atoms/common/icon/icon";
import {utcToZonedTime} from "date-fns-tz";

export class EventDetailsHelper {
  static isCancelledOrPostponedEvent(eventDetails: EventDetails): boolean {
    return this.isCancelledOrPostponedStatus(eventDetails.eventStatus);
  }

  static isCancelledOrPostponedStatus(eventStatus: EventStatus): boolean {
    return (
      eventStatus === EventStatus.Cancelled ||
      eventStatus === EventStatus.Postponed
    );
  }

  static getDateTimeText(
    eventDetails: EventDetails,
    dateFormat: string
  ): string {
    return `${this.formatOrDefault(
      eventDetails.eventDateDetails.startDate.date,
      dateFormat
    )} - ${this.formatOrDefault(
      eventDetails.eventDateDetails.endDate.date,
      dateFormat
    )}`;
  }

  private static formatOrDefault(
    date: Date | undefined | null,
    dateFormat: string
  ) {
    return date ? format(date, dateFormat) : "";
  }

  static getEventPriceText(
    t: TFunction<string[]>,
    eventDetail: EventDetails
  ): string {
    if (eventDetail.priceDetails.paidType.text === EventPaidType.free) {
      return t("event:labels:price:free");
    }
    if (eventDetail.priceDetails.paidType.text === EventPaidType.ownExpense) {
      return t("event:labels:price:ownExpense");
    }

    return (
      t("event:labels:price:currency") +
        eventDetail.priceDetails?.price?.number?.toString() ?? ""
    );
  }

  static getBookingRequirementText(
    t: TFunction<string[]>,
    eventDetails: EventDetails
  ): string {
    return eventDetails.bookingDetails.hasBookingDeadline
      ? t("event:labels:booking:hasBookingDeadline")
      : t("event:labels:booking:noBooking");
  }

  static getCancelPostponeModalMessage(
    t: TFunction<string[]>,
    eventStatus: EventStatus,
    eventTitle: string,
    eventIsBookable: boolean,
    eventIsPaid: boolean,
    numberOfBookings: number,
    numberOfRefunds: number,
    isCancellingEventSeries: boolean
  ): string {
    const fullMessage = (
      confirmMessage: string,
      eventTitle: string,
      whenBookingMessage: string,
      numberOfBookings: number,
      whenRefundMessage: string,
      numberOfRefunds: number
    ) =>
      [
        t(confirmMessage, {eventTitle: eventTitle}),
        numberOfBookings > 0
          ? t(whenBookingMessage, {count: numberOfBookings})
          : null,
        numberOfRefunds > 0
          ? t(whenRefundMessage, {count: numberOfRefunds})
          : null
      ]
        .filter(Boolean)
        .join(" ");

    switch (eventStatus) {
      case EventStatus.Cancelled:
        return isCancellingEventSeries
          ? fullMessage(
              "event:messages.confirmCancelEventSeries",
              eventTitle,
              "event:messages.confirmCancelEventSeriesPossibleBookings",
              numberOfBookings,
              "event:messages.confirmCancelEventSeriesPossibleRefunds",
              numberOfRefunds
            )
          : fullMessage(
              "event:messages.confirmCancelEvent",
              eventTitle,
              "event:messages.confirmCancelEventWhenBookings",
              numberOfBookings,
              "event:messages.confirmCancelPostponeEventWhenBookingsWhenRefunds",
              numberOfRefunds
            );
      case EventStatus.Postponed:
        return fullMessage(
          "event:messages.confirmPostponeEvent",
          eventTitle,
          "event:messages.confirmPostponeEventWhenBookings",
          numberOfBookings,
          "event:messages.confirmCancelPostponeEventWhenBookingsWhenRefunds",
          numberOfRefunds
        );
      default:
        return "";
    }
  }

  static getCancelPostponeModalTitle(
    t: TFunction<string[]>,
    eventStatus: EventStatus,
    isEditingEventSeries: boolean
  ): string {
    switch (eventStatus) {
      case EventStatus.Postponed:
        return t("event:titles.postpone");
      case EventStatus.Cancelled:
        return isEditingEventSeries
          ? t("event:titles.cancelSeries")
          : t("event:titles.cancel");
      default:
        return "";
    }
  }

  static getCancelPostponeModalPrimaryButtonText(
    t: TFunction<string[]>,
    eventStatus: EventStatus,
    isEditingEventSeries: boolean
  ): string {
    switch (eventStatus) {
      case EventStatus.Cancelled:
        return t("event:actions.confirmCancel");
      case EventStatus.Postponed:
        return t("event:actions.confirmPostpone");
      default:
        return "";
    }
  }

  static getCancelPostponeReasonPlaceholder(
    t: TFunction<string[]>,
    eventStatus: EventStatus,
    isEditingEventSeries: boolean
  ): string {
    switch (eventStatus) {
      case EventStatus.Cancelled:
        return isEditingEventSeries
          ? t("event:placeholders.cancelSeries")
          : t("event:placeholders.cancel");
      case EventStatus.Postponed:
        return t("event:placeholders.postpone");
      default:
        return "";
    }
  }

  static getEventStatusTag(t: TFunction<string[]>, eventStatus: EventStatus) {
    let tString: string;
    let tagType: TagType;

    switch (eventStatus) {
      case EventStatus.Cancelled:
        tString = "event:labels.cancelled";
        tagType = "negative";
        break;
      case EventStatus.Postponed:
        tString = "event:labels.postponed";
        tagType = "negative";
        break;
      case EventStatus.Published:
        tString = "event:labels.published";
        tagType = "success";
        break;
      default:
        tString = "event:labels.draft";
        tagType = "neutral";
    }
    return <Tag text={t(tString)} type={tagType} />;
  }

  static getEventReleaseTag = (
    t: TFunction<string[]>,
    isReleased?: boolean
  ) => (
    <Tag
      text={t(
        isReleased
          ? "event:labels.calendar.released"
          : "event:labels.calendar.unreleased"
      )}
      type={isReleased ? "success" : "neutral"}
    />
  );

  static canEditEventSeries(eventDetails: EventDetails) {
    const filter = [
      RecurringStatus.Series,
      RecurringStatus.DetachedOccurrence,
      RecurringStatus.DetachedSeries
    ];
    return filter.includes(eventDetails.recurringStatus);
  }

  static isSeries(eventDetails: EventDetails) {
    const filter = [RecurringStatus.Series, RecurringStatus.DetachedSeries];
    return filter.includes(eventDetails.recurringStatus);
  }

  static isUnreleasedEventSeries(eventDetails: EventDetails) {
    const isEventSeries = this.isSeries(eventDetails);
    return isEventSeries && !eventDetails.isReleased;
  }

  static getRecurringEventStatusIcon = (eventDetails: EventDetails) => {
    let source: IconSource;
    switch (eventDetails.recurringStatus) {
      case RecurringStatus.Series:
      case RecurringStatus.DetachedSeries:
        source = IconSource.series;
        break;
      case RecurringStatus.DetachedOccurrence:
        source = IconSource.detachedOccurrence;
        break;
      default:
        return;
    }

    return (
      <span className={"recurring-status-icon"}>
        <Icon source={source} />
      </span>
    );
  };

  static getPreReleasedPill = (
    t: TFunction<string[]>,
    eventDetails: EventDetails,
    currentDateTime: Date,
    isCurrentEventReleased: boolean
  ) => {
    if (!eventDetails.releaseDate?.date) {
      return undefined;
    }

    const releaseDate = utcToZonedTime(
      eventDetails.releaseDate.date,
      eventDetails.timeZone
    );
    const isPassedEndDate = eventDetails.isPassedEndDate!;
    const isFutureRelease = releaseDate >= currentDateTime && !isPassedEndDate;
    const isPreReleased =
      releaseDate <= currentDateTime && !isCurrentEventReleased;

    if (isFutureRelease || isPreReleased) {
      return (
        <Tag
          text={t(
            isFutureRelease
              ? "event:labels.calendar.preRelease"
              : "event:labels.calendar.preReleased"
          )}
          type={isFutureRelease ? "warning" : "digital"}
        />
      );
    }
  };

  static isBookableEvent(eventDetails: EventDetails) {
    return (
      eventDetails.bookingDetails.capacity.number !== 0 ||
      eventDetails.bookingDetails.hasBookingDeadline
    );
  }
}
